import { useTranslation } from 'react-i18next'
import { ReportChart } from '@/components/relatorios/ReportChart'
import { ReportKpiCard } from '@/components/relatorios/ReportKpiCard'
import { ReportShell } from '@/components/relatorios/ReportShell'
import { ReportTable } from '@/components/relatorios/ReportTable'
import { exportCsv } from '@/components/relatorios/reportUtils'
import { useSalesReport } from '@/hooks/reports/reportHooks'
import { useReportDateRange } from '@/hooks/reports/useReportDateRange'
import { useCurrency } from '@/hooks/useCurrency'
import { formatDayKey } from '@/lib/accountTimezone'

export default function VendasReport() {
  const { t } = useTranslation('reports')
  const { formatCurrency } = useCurrency()
  const [range, setRange] = useReportDateRange()
  const report = useSalesReport(range)
  const won = report.data.rows.reduce((sum, row) => sum + row.won, 0)
  const lost = report.data.rows.reduce((sum, row) => sum + row.lost, 0)
  const revenue = report.data.rows.reduce((sum, row) => sum + row.revenue, 0)

  return (
    <ReportShell
      title={t('sales.title')}
      description={t('sales.description')}
      {...range}
      onRangeChange={setRange}
      loading={report.loading}
      error={report.error}
      empty={won + lost === 0}
      onRetry={report.refetch}
      onExport={() => exportCsv('vendas', report.data.rows, [
        { label: t('fields.date'), value: (row) => row.date },
        { label: t('fields.won'), value: (row) => row.won },
        { label: t('fields.lost'), value: (row) => row.lost },
        { label: t('fields.revenue'), value: (row) => row.revenue.toFixed(2).replace('.', ',') },
      ])}
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <ReportKpiCard label={t('sales.kpiWon')} value={won} />
        <ReportKpiCard label={t('sales.kpiLost')} value={lost} />
        <ReportKpiCard label={t('fields.revenue')} value={formatCurrency(revenue)} />
        <ReportKpiCard label={t('fields.avgTicket')} value={formatCurrency(won ? revenue / won : 0)} />
      </div>
      <ReportChart
        type="bar"
        data={report.data.rows.map((row) => ({ ...row, label: formatDayKey(row.date, { day: '2-digit', month: '2-digit' }) }))}
        xKey="label"
        series={[
          { key: 'won', label: t('fields.won'), color: 'var(--chart-2)' },
          { key: 'lost', label: t('fields.lost'), color: 'var(--chart-5)' },
        ]}
      />
      <div className="grid gap-4 xl:grid-cols-2">
        <ReportTable
          rows={report.data.rows.filter((row) => row.won + row.lost > 0)}
          rowKey={(row) => row.date}
          columns={[
            { key: 'date', label: t('fields.date'), render: (row) => formatDayKey(row.date) },
            { key: 'won', label: t('fields.won'), align: 'right', render: (row) => row.won },
            { key: 'lost', label: t('fields.lost'), align: 'right', render: (row) => row.lost },
            { key: 'revenue', label: t('fields.revenue'), align: 'right', render: (row) => formatCurrency(row.revenue) },
          ]}
        />
        <ReportTable
          rows={report.data.lossReasons}
          rowKey={(row) => row.reason}
          columns={[
            { key: 'reason', label: t('fields.lossReason'), render: (row) => row.reason },
            { key: 'count', label: t('fields.count'), align: 'right', render: (row) => row.count },
          ]}
        />
      </div>
    </ReportShell>
  )
}
